// src/pages/agent-dashboard/index.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PerformanceMetrics from './components/PerformanceMetrics';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import ActiveListings from './components/ActiveListings';
import LeadManagement from './components/LeadManagement';
import UpcomingShowings from './components/UpcomingShowings';
import AnalyticsSection from './components/AnalyticsSection';
import QuickListingForm from './components/QuickListingForm';
import QuickProductForm from './components/QuickProductForm';
import BidManagement from './components/BidManagement';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { apiRequest } from '../../services/apiClient';

const toDateInputValue = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().slice(0, 10);
};

const extractLocationFields = (locationValue) => {
  const parts = `${locationValue || ''}`
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return {
      farmLocation: parts.slice(0, -2).join(', '),
      city: parts[parts.length - 2],
      country: parts[parts.length - 1],
    };
  }

  if (parts.length === 2) {
    return {
      farmLocation: '',
      city: parts[0],
      country: parts[1],
    };
  }

  if (parts.length === 1) {
    return {
      farmLocation: '',
      city: parts[0],
      country: '',
    };
  }

  return {
    farmLocation: '',
    city: '',
    country: '',
  };
};

const deriveProductName = (title, productType) => {
  const normalizedTitle = `${title || ''}`.trim();
  const normalizedType = `${productType || ''}`.trim();

  if (!normalizedTitle) {
    return '';
  }

  if (normalizedType && normalizedTitle.endsWith(` - ${normalizedType}`)) {
    return normalizedTitle.slice(0, -(` - ${normalizedType}`).length).trim();
  }

  return normalizedTitle;
};

const buildEditInitialValues = (product) => {
  if (!product) {
    return null;
  }

  const locationFields = extractLocationFields(product.location);
  const normalizedProductType = `${product?.productType || ''}`.trim();

  return {
    id: product.id,
    productGroup: product?.category || '',
    productName: deriveProductName(product?.title, normalizedProductType),
    productType: normalizedProductType,
    quantity: Number.isFinite(Number(product?.quantity)) ? String(product.quantity) : '',
    unit: product?.unit || 'Kilogram (kg)',
    pricePerUnit: Number.isFinite(Number(product?.price)) ? String(product.price) : '',
    harvestDate: toDateInputValue(product?.harvestDate),
    availableDate: toDateInputValue(product?.availableDate),
    allowPartialSale: Boolean(product?.allowPartialSale),
    openToOffer: Boolean(product?.openToOffer),
    productFeatures: Array.isArray(product?.productFeatures)
      ? product.productFeatures
      : product?.isOrganic
        ? ['Organik']
        : [],
    city: locationFields.city,
    country: locationFields.country,
    farmLocation: locationFields.farmLocation,
    productDescription: product?.description || '',
    existingImageUrl: product?.imageUrl || '',
    existingImageUrls: Array.isArray(product?.images) ? product.images.filter(Boolean) : [],
  };
};

const AgentDashboard = () => {
  const location = useLocation();
  const { role } = useAuth();
  const { success: toastSuccess, error: toastError, info: toastInfo } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [showQuickListingForm, setShowQuickListingForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedListings, setSelectedListings] = useState([]);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const isMarketUserMode =
    `${role || ''}`.toUpperCase() === 'MARKET_USER'
    || ['/market-dashboard', '/dashboard'].includes(location.pathname);
  const editInitialValues = useMemo(() => buildEditInitialValues(editingProduct), [editingProduct]);

  useEffect(() => {
    // Simulate initial data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);

    // Simulate real-time notifications
    const notificationTimer = setInterval(() => {
      const mockNotifications = [
        'New lead inquiry for 123 Oak Street',
        'Price reduction approved for 456 Pine Ave',
        'Showing scheduled for tomorrow at 2 PM'
      ];
      const randomNotification = mockNotifications[Math.floor(Math.random() * mockNotifications.length)];
      setNotifications(prev => [{
        id: Date.now(),
        message: randomNotification,
        timestamp: new Date()
      }, ...prev.slice(0, 4)]);
    }, 30000);

    return () => {
      clearTimeout(timer);
      clearInterval(notificationTimer);
    };
  }, []);

  const handleBulkAction = (action, listingIds) => {
    console.log(`Bulk action ${action} for listings:`, listingIds);
    // Implement bulk action logic
  };

  const handleQuickListing = () => {
    setShowQuickListingForm(true);
  };

  const uploadProductImagesIfProvided = async (images) => {
    const files = Array.isArray(images) ? images.filter((image) => image instanceof File) : [];
    if (!files.length) {
      return [];
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const uploadResult = await apiRequest('/market/products/upload-images', {
      method: 'POST',
      auth: true,
      body: formData,
    });

    const imageUrls = Array.isArray(uploadResult?.imageUrls)
      ? uploadResult.imageUrls.filter(Boolean)
      : [];

    return imageUrls;
  };

  const handleListingSubmit = async (listingData) => {
    if (!isMarketUserMode) {
      console.log('New listing data:', listingData);
      toastInfo('Bu akış yalnızca pazaryeri kullanıcıları için aktiftir.');
      setShowQuickListingForm(false);
      return;
    }

    try {
      const locationParts = [
        listingData?.farmLocation?.trim(),
        listingData?.city?.trim(),
        listingData?.country?.trim(),
      ].filter(Boolean);
      const uploadedImageUrls = await uploadProductImagesIfProvided(listingData?.productImages);

      const title = listingData?.productName?.trim()
        ? `${listingData.productName}${listingData?.productType ? ` - ${listingData.productType}` : ''}`
        : listingData?.productType?.trim() || 'Untitled Product';

      const payload = {
        title,
        description:
          listingData?.productDescription?.trim() ||
          [listingData?.productGroup, listingData?.productName].filter(Boolean).join(' / ') ||
          undefined,
        price: Number(listingData?.pricePerUnit),
        quantity: Number(listingData?.quantity),
        unit: listingData?.unit || 'Kilogram (kg)',
        productType: listingData?.productType || undefined,
        category: listingData?.productGroup || undefined,
        location: locationParts.join(', ') || undefined,
        harvestDate: listingData?.harvestDate || undefined,
        availableDate: listingData?.availableDate || undefined,
        imageUrls: uploadedImageUrls.length ? uploadedImageUrls : undefined,
        imageUrl: uploadedImageUrls[0] || undefined,
        allowPartialSale: Boolean(listingData?.allowPartialSale),
        openToOffer: Boolean(listingData?.openToOffer),
        productFeatures: Array.isArray(listingData?.productFeatures)
          ? listingData.productFeatures
          : undefined,
        isOrganic: Array.isArray(listingData?.productFeatures) && listingData.productFeatures.includes('Organik'),
      };

      await apiRequest('/market/products', {
        method: 'POST',
        auth: true,
        body: payload,
      });

      toastSuccess('Ürün başarıyla oluşturuldu.');
      setProductsRefreshKey((prev) => prev + 1);
      setShowQuickListingForm(false);
    } catch (error) {
      toastError(error?.message || 'Ürün oluşturulurken hata oluştu.');
      throw error;
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
  };

  const handleEditProductImagesPersist = async (nextImageUrls) => {
    if (!editingProduct?.id) {
      throw new Error('Düzenlenen ürün bulunamadı.');
    }

    const normalizedImageUrls = Array.isArray(nextImageUrls)
      ? nextImageUrls.map((url) => `${url || ''}`.trim()).filter(Boolean).slice(0, 5)
      : [];

    const updated = await apiRequest(`/market/products/${editingProduct.id}`, {
      method: 'PATCH',
      auth: true,
      body: {
        imageUrls: normalizedImageUrls,
      },
    });

    setEditingProduct(updated);
    setProductsRefreshKey((prev) => prev + 1);
    return updated;
  };

  const handleProductUpdate = async (formData) => {
    if (!editingProduct?.id) {
      return;
    }

    try {
      const locationParts = [
        formData?.farmLocation?.trim(),
        formData?.city?.trim(),
        formData?.country?.trim(),
      ].filter(Boolean);

      const uploadedImageUrls = await uploadProductImagesIfProvided(formData?.productImages);
      const existingImageUrls = Array.isArray(editingProduct?.images)
        ? editingProduct.images.filter(Boolean)
        : editingProduct?.imageUrl
          ? [editingProduct.imageUrl]
          : [];
      const nextImageUrls = uploadedImageUrls.length ? uploadedImageUrls : existingImageUrls;
      const title = formData?.productName?.trim()
        ? `${formData.productName}${formData?.productType ? ` - ${formData.productType}` : ''}`
        : formData?.productType?.trim() || editingProduct?.title || 'Untitled Product';

      const payload = {
        title,
        description:
          formData?.productDescription?.trim() ||
          [formData?.productGroup, formData?.productName].filter(Boolean).join(' / ') ||
          undefined,
        price: Number(formData?.pricePerUnit),
        quantity: Number(formData?.quantity),
        unit: formData?.unit || 'Kilogram (kg)',
        productType: formData?.productType || undefined,
        category: formData?.productGroup || undefined,
        location: locationParts.join(', ') || undefined,
        harvestDate: formData?.harvestDate || undefined,
        availableDate: formData?.availableDate || undefined,
        imageUrls: nextImageUrls.length ? nextImageUrls : undefined,
        imageUrl: nextImageUrls[0] || undefined,
        allowPartialSale: Boolean(formData?.allowPartialSale),
        openToOffer: Boolean(formData?.openToOffer),
        productFeatures: Array.isArray(formData?.productFeatures)
          ? formData.productFeatures
          : undefined,
        isOrganic: Array.isArray(formData?.productFeatures) && formData.productFeatures.includes('Organik'),
      };

      await apiRequest(`/market/products/${editingProduct.id}`, {
        method: 'PATCH',
        auth: true,
        body: payload,
      });

      toastSuccess('Ürün güncellendi.');
      setProductsRefreshKey((prev) => prev + 1);
      setEditingProduct(null);
    } catch (error) {
      toastError(error?.message || 'Ürün güncellenirken hata oluştu.');
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Dashboard Skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-secondary-100 rounded w-1/3 mb-4 skeleton"></div>
              <div className="h-4 bg-secondary-100 rounded w-1/2 skeleton"></div>
            </div>
            
            {/* Metrics Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-surface p-6 rounded-lg shadow-elevation-1">
                  <div className="h-4 bg-secondary-100 rounded w-1/2 mb-2 skeleton"></div>
                  <div className="h-8 bg-secondary-100 rounded w-3/4 skeleton"></div>
                </div>
              ))}
            </div>
            
            {/* Content Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="h-64 bg-secondary-100 rounded-lg skeleton mb-8"></div>
                <div className="h-96 bg-secondary-100 rounded-lg skeleton"></div>
              </div>
              <div className="space-y-8">
                <div className="h-64 bg-secondary-100 rounded-lg skeleton"></div>
                <div className="h-64 bg-secondary-100 rounded-lg skeleton"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary font-heading mb-2">
                  {isMarketUserMode ? 'Pazaryeri Paneli' : 'Danışman Paneli'}
                </h1>
                <p className="text-text-secondary">
                  {isMarketUserMode
                    ? 'Ürünlerini, stok görünürlüğünü ve performansını yönet.'
                    : 'İlanlarını yönet, potansiyel alıcıları takip et ve performansını izle.'}
                </p>
              </div>
              <div className="mt-4 md:mt-0">
                <button
                  onClick={handleQuickListing}
                  className="bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-primary-700 transition-all duration-200 ease-out micro-interaction shadow-elevation-1"
                >
                  {isMarketUserMode ? 'Yeni Ürün Oluştur' : 'Yeni İlan Oluştur'}
                </button>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <PerformanceMetrics />

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* Quick Actions */}
              <QuickActions onQuickListing={handleQuickListing} isSellerMode={isMarketUserMode} />
              
              {/* Active Listings Table */}
              <ActiveListings 
                selectedListings={selectedListings}
                onSelectionChange={setSelectedListings}
                onBulkAction={handleBulkAction}
                onEditProduct={handleEditProduct}
                refreshKey={productsRefreshKey}
              />

              {isMarketUserMode ? (
                <BidManagement
                  refreshKey={productsRefreshKey}
                  onBidActionComplete={() => setProductsRefreshKey((prev) => prev + 1)}
                />
              ) : null}
              
              {/* Analytics Section */}
              <AnalyticsSection />
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Recent Activity */}
              <RecentActivity notifications={notifications} />
              
              {/* Lead Management */}
              <LeadManagement />
              
              {/* Upcoming Showings */}
              <UpcomingShowings />
            </div>
          </div>
        </div>
      </main>

      {/* Quick Listing Form Modal */}
      {showQuickListingForm && (
        isMarketUserMode ? (
          <QuickProductForm
            onClose={() => setShowQuickListingForm(false)}
            onSubmit={handleListingSubmit}
          />
        ) : (
          <QuickListingForm 
            onClose={() => setShowQuickListingForm(false)}
            onSubmit={handleListingSubmit}
          />
        )
      )}

      {isMarketUserMode && editingProduct && (
        <QuickProductForm
          mode="edit"
          initialValues={editInitialValues}
          onClose={() => setEditingProduct(null)}
          onSubmit={handleProductUpdate}
          onEditImagesPersist={handleEditProductImagesPersist}
        />
      )}
    </div>
  );
};

export default AgentDashboard;
